import { Controller } from "@hotwired/stimulus";

/**
 * Moves a full-width image up or down by one grid
 * row within its sortable container, then submits
 * the sort form to persist the new order.
 *
 * The number of positions to skip is determined by
 * the grid's computed column count so the image
 * always lands at a row boundary.
 */
export default class extends Controller {
  static values = {
    formId: String,
  };

  moveUp() {
    const container = this.sortableContainer;
    if (!container) return;

    const cols = this.computeGridCols(container);
    const items = this.regularItems(container);
    const idx = this.indexAmongAll(container);
    if (idx <= 0) return;

    // Count `cols` regular items before us,
    // then insert before the earliest one found.
    let count = 0;
    let target = null;
    let sibling = this.element.previousElementSibling;
    while (sibling && count < cols) {
      if (!this.isFullWidth(sibling)) count++;
      target = sibling;
      sibling = sibling.previousElementSibling;
    }

    if (target && count > 0) {
      container.insertBefore(this.element, target);
      this.submitForm();
    }
  }

  moveDown() {
    const container = this.sortableContainer;
    if (!container) return;

    const cols = this.computeGridCols(container);
    const idx = this.indexAmongAll(container);
    const total = container.children.length;
    if (idx >= total - 1) return;

    // Count `cols` regular items after us,
    // then insert after the last one found.
    let count = 0;
    let lastSkipped = null;
    let sibling = this.element.nextElementSibling;
    while (sibling && count < cols) {
      if (!this.isFullWidth(sibling)) count++;
      lastSkipped = sibling;
      sibling = sibling.nextElementSibling;
    }

    if (count === 0) return;

    // Insert after lastSkipped
    if (lastSkipped.nextElementSibling) {
      container.insertBefore(
        this.element,
        lastSkipped.nextElementSibling
      );
    } else {
      container.appendChild(this.element);
    }
    this.submitForm();
  }

  isFullWidth(el) {
    return el.classList.contains("col-span-full");
  }

  indexAmongAll(container) {
    return Array.from(container.children).indexOf(
      this.element
    );
  }

  regularItems(container) {
    return Array.from(container.children).filter(
      (el) => !this.isFullWidth(el)
    );
  }

  submitForm() {
    const form = document.getElementById(
      this.formIdValue
    );
    if (form) form.requestSubmit();
  }

  get sortableContainer() {
    return this.element.closest(
      "[data-sortable-target='container']"
    );
  }

  computeGridCols(container) {
    const style = getComputedStyle(container);
    const template = style.getPropertyValue(
      "grid-template-columns"
    );
    if (!template || template === "none") return 1;
    return template.split(" ").length;
  }
}
